/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { PageHeader } from "../../../../components/page-skeleton/PageHeader";
import { FormRow } from "../../../common/hook-form";
import { useFormContext } from "react-hook-form";
import { CardContainer } from "../../../../components/page-skeleton/CardContainer";
import { Country } from "../../../../model/Country";
import { CardBody } from "../../../../components/page-skeleton/CardBody";
import { SelectInput } from "../../../../components/forms/inputs/SelectInput";
import { CtaAction } from "../../../../service/api-response/AppCallToAction";
import { TextInput } from "../../../../components/forms/inputs/TextInput";
import { SelectCountryInput } from "../../../../components/forms/inputs/SelectCountryInput";
import { SelectLocaleInput } from "../../../../components/forms/inputs/SelectLocaleInput";

const typeStyle = css`
  margin-bottom: 8px;
`;

const cardBodyAdditionalStyling = css`
  padding-bottom: 24px;
  gap: 16px;
`;

type Props = {
  countries: Country[];
};

export const CtaGeneralInformation = ({ countries }: Props) => {
  const { watch } = useFormContext();

  const action = watch("action");

  const countryOptions = (countries || [])
    .filter((c) => c.enabled)
    .sort()
    .map((o) => ({
      label: o.countryCode,
      value: o.countryCode,
    }));

  countryOptions?.unshift({
    label: "",
    value: "",
  });

  const actionOptions: { label: string; value: CtaAction }[] = [
    {
      label: "Discount card",
      value: "discount_card",
    },
    {
      label: "Punch card",
      value: "punch_card",
    },
    {
      label: "Add to order",
      value: "add_to_order",
    },
    {
      label: "Table booking",
      value: "table_booking",
    },
    {
      value: "external",
      label: "External link",
    },
  ];

  return (
    <CardContainer>
      <PageHeader
        title="General call to action information"
        subtitle="Configure the general settings for this call to action."
      />
      <CardBody css={cardBodyAdditionalStyling}>
        <FormRow css={typeStyle}>
          <SelectInput
            label="Action"
            name="action"
            options={actionOptions}
            helperText="This is the type of CTA that you are creating. All will appear on the app homepage."
          />
        </FormRow>
        <FormRow>
          <TextInput
            label="Title"
            name="title"
            helperText="The title of the call to action."
          />
          <TextInput
            label="Description"
            name="description"
            helperText="Description that will appear under the title."
          />
        </FormRow>
        <FormRow>
          <SelectCountryInput name="countryCode" />
          <SelectLocaleInput name="locale" />
          <TextInput label="Sort key" name="sortKey" />
        </FormRow>
        {action === "external" && (
          <FormRow>
            <TextInput
              label="External URL"
              name="externalUrl"
              helperText="The URL of the external link."
            />
          </FormRow>
        )}
      </CardBody>
    </CardContainer>
  );
};
